import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react'

import { createContext } from 'use-context-selector'

import { HandleApiErrors } from '@shared/clients/http/commons/HandleApiErrors'
import { UrlConfig } from '@shared/configs/URL'
import { Storage } from '@shared/constants/Storage'
import { usePersistedState } from '@shared/utils/usePersistedState'

import { Tokens } from '@modules/auth/types/Auth/auth'
import { AuthContextProps } from '@modules/auth/types/Auth/context'
import { SignInByTokensRequest } from '@modules/auth/types/Auth/requests'

const AuthContext = createContext({} as AuthContextProps)

AuthContext.displayName = 'Auth'

const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const getTokensStored = (): Tokens | null => {
    const tokensStored = localStorage.getItem(Storage.TOKENS)
    return tokensStored ? JSON.parse(tokensStored) : null
  }

  const [isAuthenticated, setIsAuthenticated] = useState(() => !!getTokensStored())
  const [tokens, setTokens] = usePersistedState<Tokens | null>(Storage.TOKENS, getTokensStored())

  const signInByTokens = useCallback(
    async (data: SignInByTokensRequest) => {
      try {
        const { accessToken, customToken, refreshToken } = data

        setIsAuthenticated(true)
        setTokens({ accessToken, customToken, refreshToken })
      } catch (err) {
        HandleApiErrors.handle({ err })
      }
    },
    [setTokens],
  )

  const signOut = useCallback(async () => {
    setIsAuthenticated(false)
    setTokens(null)

    localStorage.removeItem(Storage.CURRENT_PROFILE)

    window.location.href = UrlConfig.clientAuthUrl
  }, [setTokens])

  const contextValue = useMemo<AuthContextProps>(
    () => ({ isAuthenticated, tokens, signInByTokens, signOut }),
    [isAuthenticated, signInByTokens, signOut, tokens],
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export { AuthContext, AuthProvider }
